import * as React from 'react';
import type { ReactNode } from 'react';
import styles from './RadioGroup.module.css';
import { RadioGroupProvider } from './RadioGroupContext';

export interface RadioGroupBaseProps {
    name: string;
    onChange: (id: string) => void;
    children: ReactNode;
    disabled?: boolean;
    currentValue?: string;
    className?: string;
}

interface RadioGroupWithLabel extends RadioGroupBaseProps {
    label: string;
}

export type RadioGroupWithAria = Required<Pick<React.FieldsetHTMLAttributes<HTMLFieldSetElement>, 'aria-describedby'>> &
    RadioGroupBaseProps;

type RadioGroupProps = (RadioGroupWithLabel | RadioGroupWithAria) & RadioGroupBaseProps;

export const RadioGroup: React.FC<React.PropsWithChildren<RadioGroupProps>> = props => {
    const { name, onChange, currentValue, disabled, className, children, ...restProps } = props;
    const hasLabel = 'label' in props && props.label;

    const [currentGroupValue, setCurrentGroupValue] = React.useState(currentValue);

    React.useEffect(() => {
        setCurrentGroupValue(currentValue);
    }, [currentValue]);

    const handleOnChange = (checkedId: string) => {
        setCurrentGroupValue(checkedId);
        onChange && onChange(checkedId);
    };

    return (
        <RadioGroupProvider name={name} onChange={handleOnChange} currentValue={currentGroupValue} disabled={disabled}>
            <fieldset {...restProps} className={className}>
                {hasLabel && <legend>{props.label}</legend>}
                <div className={styles.Radios}>{children}</div>
            </fieldset>
        </RadioGroupProvider>
    );
};
